/**
 * journeyPhase
 *
 * Where a patient stands on the four-rung journey map, and which journey that
 * map is describing.
 *
 * Lifted out of `RoadmapModal` so the rules can be asserted. The screen it came
 * from imports React Native, which a node test cannot load — which is why a
 * defect this plain (any freezing cycle in the whole history outranking the
 * cycle she just closed) survived in it unnoticed.
 */

import type { Cycle } from "@src/packages/cycles/hooks/useCycles";

export const PHASE_IDS = [
  "deciding",
  "preparing",
  "in-treatment",
  "storing",
] as const;

export type RoadmapPhaseId = (typeof PHASE_IDS)[number];

/** Cycle types whose output is put away for later. */
const FREEZING_CYCLE_TYPES = new Set([
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

/** Cycle types run to make a pregnancy happen now, not to store anything. */
const PREGNANCY_CYCLE_TYPES = new Set(["ivf", "icsi", "iui", "fet", "oi"]);

/**
 * Which journey the last rung of the map is describing.
 *
 * The four rungs are the same shape for everyone, but the last one is not the
 * same thing on both paths: a freezing cycle ends with material in storage, a
 * transfer ends with a result. It used to say "storing" either way, which is
 * meaningless to a patient who has just had an embryo transferred.
 */
export type JourneyPath = "preserve" | "pregnancy";

/**
 * Goals whose last rung is a result rather than a store.
 *
 * Surrogacy and adoption are deliberately absent. They are pregnancy goals in
 * the ordinary sense, but this map's pregnancy rung says "record your beta hCG
 * result", which is not something either patient will ever do — `MyJourney`
 * already treats them as non-cycle goals elsewhere. Neither rung really fits
 * them; until one is written they keep the neutral wording rather than being
 * told to record a test they are not having.
 */
const PREGNANCY_INTENTS = new Set(["pregnancy", "Conception"]);

/**
 * The cycle that says where she is now: the one that started most recently.
 *
 * Read `startDate`, not array order — nothing in the API contract promises
 * these come back sorted, and the answer is wrong rather than merely unstable
 * if they do not. A cycle with no start date sorts last: it cannot be shown to
 * be the recent one.
 */
function selectLatestCycle<T extends Pick<Cycle, "startDate">>(
  cycles: T[],
): T | null {
  let latest: T | null = null;
  for (const cycle of cycles) {
    if (!cycle.startDate) {
      continue;
    }
    if (!latest?.startDate || cycle.startDate > latest.startDate) {
      latest = cycle;
    }
  }
  // No fallback on array order: the comment above says the API promises none,
  // and answering with `cycles[0]` would have let the backend's return order
  // decide a patient's path whenever every cycle she holds is undated.
  return latest;
}

/**
 * Which path the map is describing, from what she has actually done.
 *
 * Her most recent cycle answers it; the onboarding goal only stands in when
 * there is no cycle to read. A goal set once at sign-up is the weaker signal —
 * a patient who came to freeze eggs and is now transferring an embryo has
 * changed path whatever the form says.
 */
/**
 * The cycle the map is describing: her most recent one.
 *
 * Exported so the screens gather signals about the same cycle the map names.
 * Reading a different one would tick lines against a cycle she is not looking
 * at.
 */
export function selectJourneyCycle<
  T extends Pick<Cycle, "startDate" | "status">,
>(cycles: T[] | undefined): T | null {
  // An open cycle is the one the rung is highlighting — `deriveCyclePhase`
  // answers "in-treatment" for any open cycle — so it has to be the one the
  // ticks describe too. Reading the newest instead let the rail talk about a
  // cycle still running while every tick was gathered from a closed one.
  const open = (cycles ?? []).filter((cycle) => cycle.status === "open");
  return selectLatestCycle(open.length ? open : (cycles ?? []));
}

export function resolveJourneyPath(
  cycles: Pick<Cycle, "status" | "cycleType" | "startDate">[] | undefined,
  intent?: string | null,
): JourneyPath {
  const latest = selectLatestCycle(cycles ?? []);
  if (latest) {
    if (PREGNANCY_CYCLE_TYPES.has(latest.cycleType)) return "pregnancy";
    if (FREEZING_CYCLE_TYPES.has(latest.cycleType)) return "preserve";
  }
  return intent && PREGNANCY_INTENTS.has(intent) ? "pregnancy" : "preserve";
}

/**
 * The user's actual roadmap phase derived from their cycles — the ground truth
 * of where they are, independent of the (often stale, onboarding-only) intent.
 *
 *   - an open cycle            -> they are actively "in-treatment"
 *   - a finished cycle         -> the last rung, whatever that rung means on
 *                                 their path
 *
 * The finished case used to ask whether *any* closed cycle in the whole history
 * was a freezing one. A patient in FET necessarily has one — the embryos she is
 * transferring were frozen in an earlier cycle — so closing her transfer sent
 * her back to "storing", years after the fact. Only the most recent cycle says
 * where she is.
 *
 * Returns null when there's no cycle activity yet, so callers fall back to the
 * intent-based resolution.
 */
export function deriveCyclePhase(
  cycles: Pick<Cycle, "status" | "cycleType" | "startDate">[] | undefined,
): RoadmapPhaseId | null {
  if (!cycles?.length) return null;
  if (cycles.some((c) => c.status === "open")) return "in-treatment";
  const closed = cycles.filter((c) => c.status === "closed");
  return closed.length ? "storing" : null;
}

/**
 * The copy prefix for a rung.
 *
 * Only the last one varies. `storing` stays its position id — renaming it would
 * ripple through three screens and the catalogues for no gain — but on a
 * pregnancy path it is dressed as the result she is waiting for.
 */
export function phaseKey(id: RoadmapPhaseId, path: JourneyPath): string {
  const keys: Record<RoadmapPhaseId, string> = {
    deciding: "deciding",
    preparing: "preparing",
    "in-treatment": "in_treatment",
    storing: path === "pregnancy" ? "result" : "storing",
  };
  return keys[id];
}

/**
 * Resolve which of the four roadmap phases is "current" for a patient.
 *
 * Two signals feed in, in priority order:
 *
 *   1. `fertilityIntent.stage` from the API — the canonical onboarding
 *      signal. Maps 1:1 to a phase. This is the right answer whenever
 *      the user has filled it in.
 *   2. `fertilityIntent.goal` (API enum) or the legacy onboarding
 *      `treatmentIntent` value, both of which we historically mashed
 *      into the same string. Less specific (most goals just mean
 *      "preparing"), but better than the previous hardcoded fallback.
 *
 * Default phase is "deciding" — accurate for brand-new patients who
 * haven't picked a path yet.
 *
 * Centralised here so PlanHomeScreen and YouScreen both render the same
 * phase highlight when opening the modal (UAT issue #101 — the journey
 * map was previously hardcoded to "in-treatment" on PlanHomeScreen).
 */
export function resolveRoadmapPhaseId(
  stage: string | null | undefined,
  intent: string | null | undefined,
  cycles?: Pick<Cycle, "status" | "cycleType" | "startDate">[],
): RoadmapPhaseId {
  // Actual journey state (cycles) wins over the static intent/stage signal.
  const cyclePhase = deriveCyclePhase(cycles);
  if (cyclePhase) return cyclePhase;

  switch (stage) {
    case "in-treatment":
      return "in-treatment";
    case "getting-ready":
      return "preparing";
    case "exploring-options":
    case "trying-naturally":
      return "deciding";
  }
  switch (intent) {
    case "InTreatment":
    case "StartingTreatment":
      return "in-treatment";
    case "Preservation":
    case "Conception":
    case "Donation":
    case "egg-freezing":
    case "sperm-freezing":
    case "embryo-freezing":
    case "egg-preserve":
    case "sperm-preserve":
    case "embryo-preserve":
    case "pregnancy":
    case "egg-donation":
    case "sperm-donation":
    case "surrogacy":
    case "adoption":
      return "preparing";
    case "Exploring":
    case "exploring":
    case "learn":
      return "deciding";
  }
  return "deciding";
}
